/**
 * Marvel Commercial IPTV Uploader Specifications
 * 
 * Commercial IPTV uploads for Marvel Stadium with simplified placement options.
 * 
 * Placements:
 * - Club Sponsor IPTV Content (1920×1080)
 * 
 * Routes to commercial Airtable base via webhook.
 */

// ============================================================
// IPTV PLACEMENTS (PILLS)
// ============================================================
export const IPTV_PLACEMENTS = [
  { label: "Club Sponsor IPTV Content", placementId: "CLUB-SPONSOR" },
];

// ============================================================
// PLACEMENT → UPLOADER GROUPS
// ============================================================
export const IPTV_PLACEMENT_GROUPS = {
  "CLUB-SPONSOR": ["CLUB-SPONSOR"],
};

// ============================================================
// GROUPED FOLDER PLACEMENTS
// Commercial IPTV doesn't use grouped folders
// ============================================================
export const GROUPED_FOLDER_PLACEMENTS = new Set([]);

// ============================================================
// COMMON SPECS
// ============================================================
const IPTV_VIDEO_COMMON = {
  allowed: true,
  extensions: ["mp4"],
  formats: ["AVC"],
  frameRate: 25,
  duration: 15, // Marvel Commercial IPTV has 15s duration
  audio: { mode: "optional" },
};

const IPTV_IMAGE_COMMON = {
  allowed: true,
  extensions: ["png"],
};

// ============================================================
// SPEC FACTORY
// ============================================================
function singleUploaderSpec({ label, specId, prefix, width, height }) {
  return {
    label,
    specId,
    prefix,
    ledType: "iptv",
    contentGroup: "singleAsset",
    requiredFiles: 1,
    dimensions: [{ width, height }],
    videoSpec: { ...IPTV_VIDEO_COMMON },
    imageSpec: { ...IPTV_IMAGE_COMMON },
  };
}

// ============================================================
// UPLOADER SPECS (SINGLE SOURCE OF TRUTH)
// ============================================================
export const UPLOADER_SPECS = {
  "CLUB-SPONSOR": singleUploaderSpec({
    label: "Club Sponsor IPTV Content",
    specId: "CLUB-SPONSOR",
    prefix: "CSPON",
    width: 1920,
    height: 1080,
  }),
};

// ============================================================
// HELPER: Build human-readable spec label
// ============================================================
export function buildSpecLabel(spec) {
  const parts = [];

  // Dimensions
  if (spec.dimensions) {
    parts.push(spec.dimensions.map((d) => `${d.width}×${d.height}`).join(" / "));
  }

  // Video
  if (spec.videoSpec) {
    parts.push("MP4");
    parts.push("H.264");
    parts.push(`${spec.videoSpec.frameRate}fps`);
    if (spec.videoSpec.duration) {
      parts.push(`${spec.videoSpec.duration}s`);
    }
    parts.push("Looping");
  }

  // Image
  if (spec.imageSpec?.allowed) {
    if (Array.isArray(spec.imageSpec.extensions) && spec.imageSpec.extensions.length) {
      parts.push(
        `Static ${spec.imageSpec.extensions.map((e) => e.toUpperCase()).join(" / ")}`
      );
    } else {
      parts.push("Static Image");
    }
    parts.push("RGB");
  }

  return parts.join(" · ");
}

// ============================================================
// METADATA WEBHOOK (Commercial - routes to commercial Airtable base)
// ============================================================
export const METADATA_WEBHOOK = "https://hook.eu2.make.com/fgninplw8emfjkontq8csdhjxxc2g00v";

// ============================================================
// DEFAULT EXPORT
// ============================================================
const marvelCommercialIptvSpecs = {
  IPTV_PLACEMENTS,
  IPTV_PLACEMENT_GROUPS,
  GROUPED_FOLDER_PLACEMENTS,
  UPLOADER_SPECS,
  buildSpecLabel,
  METADATA_WEBHOOK,
};

export default marvelCommercialIptvSpecs;
